// frontend-react/src/hooks/useProjectionInsights.js
// Chunk 31 — fetches the v3 product surfaces for a player: IL risk, gap-anomaly,
// profile comps (comparables overhaul Gate 2a: the consumer comps card reads
// /profile-comps — physical-profile kNN — NOT the gap-pattern endpoint, which
// stays live for a future labeled power view).
// Status-safe; surfaces that have no data return null without erroring.
import { useState, useEffect } from 'react';
import api from '../services/apiService';

const ok = (r) => (r && r.data && r.data.status === 'ok' ? r.data : null);

export const useProjectionInsights = (playerId, { isPitcher, target } = {}) => {
  const t = target || (isPitcher ? 'ERA' : 'HR');  // Bug #4: pitcher-aware default (was always 'HR')
  const [il, setIl] = useState(null);
  const [anomaly, setAnomaly] = useState(null);
  const [comparables, setComparables] = useState(null);
  const [series, setSeries] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!playerId) return;
    let alive = true;
    setLoading(true);
    const base = `/api/analytics/players/${playerId}`;
    const playerType = isPitcher ? 'pitcher' : 'hitter';
    const jobs = [
      api.get(`${base}/il-risk`).then((r) => { if (alive) setIl(ok(r)); }).catch(() => {}),
      // Section 12 projection-trend sparkline — applies to hitters AND pitchers.
      api.get(`${base}/projection-series/${t}?player_type=${playerType}`)
        .then((r) => { if (alive) setSeries(ok(r)); }).catch(() => {}),
    ];
    // ALL players — the gap-vector archive covers pitcher targets too (DD-FIXES
    // Bug 4 fix B; the old hitter-only skip was a false premise). The display
    // target 'ERA' resolves to the archived 'ER' server-side (fix C).
    jobs.push(api.get(`${base}/gap-anomaly/${t}`).then((r) => { if (alive) setAnomaly(ok(r)); }).catch(() => {}));
    // Profile comps (Gate 2a): covered:false shapes ({reason:'pitcher'|
    // 'insufficient_sample'|'not_current'}) flow through so the card renders the
    // labeled absent states (Gate 3); null only on fetch failure/malformed.
    jobs.push(api.get(`${base}/profile-comps`)
      .then((r) => { if (alive) setComparables(r?.data && typeof r.data.covered === 'boolean' ? r.data : null); })
      .catch(() => {}));
    Promise.allSettled(jobs).finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [playerId, isPitcher, t]);

  return { il, anomaly, comparables, series, loading };
};

export default useProjectionInsights;
